import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import { TableQueryWrapper } from '@/components/TableQueryWrapper';
import config from '@/config';
import Badge from '@/design_system/Badge';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import {
  InputDateRangeValue,
  isoStringToDateString,
} from '@/design_system/InputDate/InputDateRange';
import Pagination, { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import IconDone from '@/icons/Done.svg';
import IconDownload from '@/icons/Download.svg';
import IconError from '@/icons/Error.svg';
import IconTools from '@/icons/Tools.svg';
import IconTransaction from '@/icons/Transaction.svg';
import IconValidation from '@/icons/Validation.svg';
import IconValidationWaiting from '@/icons/ValidationWaiting.svg';
import {
  getInvoiceOriginName,
  InvoiceFromGetMany,
  InvoiceStatus,
  useInvoiceDestinationName,
  useInvoices,
} from '@/models/invoice';
import { formatDate } from '@/utils/date';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { InvoiceDestination } from './components/filters/InvoiceDestinationSelect';
import { InvoiceOrigin } from './components/filters/InvoiceOriginSelect';
import { InvoicesEmptyState } from './components/InvoicesEmptyState';
import { InvoicesFilter } from './components/InvoicesFilter';

const INVOICES_PER_PAGE = 20;

export const Invoices = ({
  page,
  setPage,
  search,
  debouncedSearch,
  debouncedSetSearch,
  invoiceStatus,
  setInvoiceStatus,
  invoiceOrigin,
  setInvoiceOrigin,
  invoiceDestination,
  setInvoiceDestination,
  dateRange,
  setDateRange,
}: {
  page: number;
  setPage: (page: number) => void;
  search: string;
  debouncedSearch: string;
  debouncedSetSearch: (value: string) => void;
  invoiceStatus: InvoiceStatus | '';
  setInvoiceStatus: (invoiceStatus: InvoiceStatus | '') => void;
  invoiceOrigin: InvoiceOrigin;
  setInvoiceOrigin: (invoiceOrigin: InvoiceOrigin) => void;
  invoiceDestination: InvoiceDestination;
  setInvoiceDestination: (invoiceDestination: InvoiceDestination) => void;
  dateRange: InputDateRangeValue;
  setDateRange: (dateRange: InputDateRangeValue) => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const {
    data: { invoices, meta } = { invoices: [] },
    isLoading,
    isError,
  } = useInvoices({
    limit: INVOICES_PER_PAGE,
    offset: (page - 1) * INVOICES_PER_PAGE,
    search: debouncedSearch || undefined,
    status: invoiceStatus || undefined,
    destinationClient: invoiceDestination.type === 'client',
    destinationCompany: invoiceDestination.type === 'company' ? invoiceDestination.id : undefined,
    originCompany: invoiceOrigin.type === 'company' ? invoiceOrigin.id : undefined,
    issuingDateFrom: dateRange.start ? isoStringToDateString(dateRange.start) : undefined,
    issuingDateTo: dateRange.end ? isoStringToDateString(dateRange.end) : undefined,
  });

  const columnWidths = {
    name: 'minmax(180px, auto)',
    issuer: 'minmax(180px, 1fr)',
    recipient: 'minmax(180px, 1fr)',
    status: 'minmax(180px, 1fr)',
    dueDate: 'minmax(100px, auto)',
    amount: 'minmax(100px, auto)',
    actions: 'minmax(100px, auto)',
  };

  return (
    <Stack gap="1rem" style={{ height: '100%' }}>
      <InvoicesFilter
        setPage={setPage}
        search={search}
        debouncedSetSearch={debouncedSetSearch}
        invoiceOrigin={invoiceOrigin}
        setInvoiceOrigin={setInvoiceOrigin}
        invoiceDestination={invoiceDestination}
        setInvoiceDestination={setInvoiceDestination}
        invoiceStatus={invoiceStatus}
        setInvoiceStatus={setInvoiceStatus}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      <TableQueryWrapper isLoading={isLoading} isError={isError}>
        {!invoices.length ? (
          <InvoicesEmptyState />
        ) : (
          <>
            {!isMobile && (
              <Table
                ariaLabel={t({
                  id: 'accounting.invoices.table.label',
                  message: 'Invoices list',
                })}
                columnWidths={[
                  columnWidths.name,
                  columnWidths.issuer,
                  columnWidths.recipient,
                  columnWidths.status,
                  columnWidths.dueDate,
                  columnWidths.amount,
                  columnWidths.actions,
                ]}
              >
                <Header>
                  <Row>
                    <Column>
                      <Trans id="accounting.invoices.table.column.number">Number</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoices.table.column.issuer">Issuer</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoices.table.column.recipient">Recipient</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoices.table.column.status">Status</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoices.table.column.dueDate">Due date</Trans>
                    </Column>
                    <Column>
                      <Trans id="accounting.invoices.table.column.amount">Services amount</Trans>
                    </Column>
                    <Column
                      ariaLabel={t({
                        id: 'accounting.invoices.table.column.actions',
                        message: 'Actions',
                      })}
                    />
                  </Row>
                </Header>

                <Body>
                  {invoices.map((invoice) => (
                    <InvoiceRow key={invoice.id} invoice={invoice} />
                  ))}
                </Body>

                <PaginationFooter
                  page={page}
                  itemsPerPage={INVOICES_PER_PAGE}
                  count={meta?.count}
                  onPageChange={setPage}
                />
              </Table>
            )}

            {isMobile && (
              <>
                <GridList
                  aria-label={t({
                    id: 'accounting.invoices.table.label',
                    message: 'Invoices list',
                  })}
                  style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                >
                  {invoices.map((invoice) => (
                    <InvoiceCard key={invoice.id} invoice={invoice} />
                  ))}
                </GridList>

                <Pagination
                  page={page}
                  itemsPerPage={INVOICES_PER_PAGE}
                  count={meta?.count}
                  onPageChange={setPage}
                />
              </>
            )}
          </>
        )}
      </TableQueryWrapper>
    </Stack>
  );
};

const InvoiceRow = ({ invoice }: { invoice: InvoiceFromGetMany }) => {
  const issuer = getInvoiceOriginName(invoice);
  const recipient = useInvoiceDestinationName(invoice);

  return (
    <Row to={`/accounting/invoices/${invoice.id}`}>
      <InvoiceNumberCell invoice={invoice} />
      <Cell>{issuer}</Cell>
      <Cell>{recipient}</Cell>
      <Cell>
        <InvoiceStatusBadge invoice={invoice} />
      </Cell>
      <Cell>
        {invoice.paymentDueDate
          ? formatDate(parseISO(invoice.paymentDueDate), {
              dateStyle: 'medium',
            })
          : '-'}
      </Cell>
      <Cell justifyContent="flex-end">{formatCurrency(invoice.amount, invoice.currency)}</Cell>
      <Cell justifyContent="flex-end">
        <Stack row gap="8px">
          {invoice.transactionId && (
            <Button
              to={`/accounting/transactions/${invoice.transactionId}`}
              variant="secondary"
              size="medium"
              iconOnly
              tooltip={
                <Trans id="accounting.invoices.table.tooltip.view-transaction">
                  View transaction
                </Trans>
              }
            >
              <IconTransaction variant="view" />
            </Button>
          )}
          {!!invoice.media.length && (
            <Button
              variant="secondary"
              size="medium"
              iconOnly
              tooltip={
                <Trans id="accounting.invoices.table.tooltip.download-invoice">
                  Download invoice
                </Trans>
              }
              href={`${config.apiUrl}/invoices/${invoice.id}/media`}
              download
            >
              <IconDownload />
            </Button>
          )}
        </Stack>
      </Cell>
    </Row>
  );
};

const InvoiceNumberCell = ({ invoice }: { invoice: InvoiceFromGetMany }) => {
  return (
    <Cell isLink>
      <Stack>
        <InlineText weight="medium">{invoice.number}</InlineText>
        {invoice.issuingDate && (
          <InlineText size="200">
            {formatDate(parseISO(invoice.issuingDate), {
              dateStyle: 'medium',
            })}
          </InlineText>
        )}
      </Stack>
    </Cell>
  );
};

const InvoiceStatusBadge = ({ invoice }: { invoice: InvoiceFromGetMany }) => {
  if (invoice.status === 'pending-validation') {
    return (
      <Badge color="warning" size="small">
        <IconValidationWaiting />
        <Trans id="accounting.invoices.table.status.pending-validation">Pending validation</Trans>
      </Badge>
    );
  }

  if (invoice.status === 'handled-by-accounting') {
    return (
      <Badge color="tertiary" size="small">
        <IconValidation />
        <Trans id="accounting.invoices.table.status.handled-by-accounting">
          Handled by accounting
        </Trans>
      </Badge>
    );
  }

  if (invoice.status === 'paid') {
    return (
      <Badge color="primary" size="small">
        <IconDone />
        <span>
          <Trans id="accounting.invoices.table.status.paid">Paid</Trans>
          {invoice.paidAt && (
            <>
              {' '}
              <InlineText size="400">
                (
                {formatDate(new Date(invoice.paidAt), {
                  dateStyle: 'medium',
                })}
                )
              </InlineText>
            </>
          )}
        </span>
      </Badge>
    );
  }

  if (invoice.status === 'issue') {
    return (
      <Badge color="danger" size="small">
        <IconError />
        <Trans id="accounting.invoices.table.status.issue">Issue reported</Trans>
      </Badge>
    );
  }

  return null;
};

const InvoiceCard = ({ invoice }: { invoice: InvoiceFromGetMany }) => {
  const issuer = getInvoiceOriginName(invoice);
  const recipient = useInvoiceDestinationName(invoice);

  return (
    <GridListItem textValue={invoice.number} href={`/accounting/invoices/${invoice.id}`}>
      <Box padding="16px" role="row" gap="8px">
        <Stack row alignItems="center" justifyContent="space-between">
          <InlineText weight="medium" color="secondary">
            {invoice.number}
          </InlineText>
          <InvoiceStatusBadge invoice={invoice} />
        </Stack>
        <Stack>
          <InlineText>{issuer}</InlineText>
          <InlineText size="200" color="secondary">
            <Trans id="accounting.invoices.table.recipient.to">To: {recipient}</Trans>
          </InlineText>
        </Stack>
        <div style={{ marginTop: '4px' }}>
          <Badge color="neutral" size="large" borderRadius="soft" hasBorder>
            <IconTools />
            <Trans id="accounting.invoices.table.amount">
              Services amount: {formatCurrency(invoice.amount, invoice.currency)}
            </Trans>
          </Badge>
        </div>
      </Box>
    </GridListItem>
  );
};
